import type { AdminCompany, AdminSector } from '../../../../services/adminService';

export type CompanyLinkStatus =
  | 'matched'
  | 'company_not_found'
  | 'wrong_industry'
  | 'wrong_sector'
  | 'sector_not_found'
  | 'sector_missing';

export type CompanyLinkTarget = {
  key: string;
  companyName: string;
  sectorName: string;
  rowNumbers: number[];
};

export type CompanyLinkResolution = CompanyLinkTarget & {
  status: CompanyLinkStatus;
  message: string;
  companyId?: number;
  sectorId?: number;
  resolvedIndustryName?: string;
  resolvedSectorName?: string;
};

export function normalizeExcelName(value: string | null | undefined): string {
  return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

export function buildCompanyLinkKey(companyName: string, sectorName: string): string {
  return `${normalizeExcelName(companyName)}::${normalizeExcelName(sectorName)}`;
}

export function collectCompanyLinkTargets(
  rows: { rowNumber: number; values: { companyName: string | null; sectorName: string | null } }[],
): CompanyLinkTarget[] {
  const map = new Map<string, CompanyLinkTarget>();

  for (const row of rows) {
    const companyName = row.values.companyName?.trim() || '';
    const sectorName = row.values.sectorName?.trim() || '';
    const key = buildCompanyLinkKey(companyName, sectorName);

    const existing = map.get(key);
    if (existing) {
      existing.rowNumbers.push(row.rowNumber);
      continue;
    }

    map.set(key, {
      key,
      companyName,
      sectorName,
      rowNumbers: [row.rowNumber],
    });
  }

  return Array.from(map.values()).sort((a, b) => a.rowNumbers[0] - b.rowNumbers[0]);
}

function findSectorByName(sectors: AdminSector[], sectorName: string): AdminSector | undefined {
  const norm = normalizeExcelName(sectorName);
  return sectors.find((s) => normalizeExcelName(s.name) === norm);
}

function exactCompanyRows(rows: AdminCompany[], companyName: string): AdminCompany[] {
  const norm = normalizeExcelName(companyName);
  return rows.filter((c) => normalizeExcelName(c.name) === norm);
}

export function resolveCompanyLinks(params: {
  industryId: number;
  industryName: string;
  sectors: AdminSector[];
  companiesInIndustry: AdminCompany[];
  globalCompanies: AdminCompany[];
  targets: CompanyLinkTarget[];
  overrides?: Record<string, { companyId: number; sectorId: number }>;
}): CompanyLinkResolution[] {
  const {
    industryId,
    industryName,
    sectors,
    companiesInIndustry,
    globalCompanies,
    targets,
    overrides = {},
  } = params;

  return targets.map((target) => {
    const override = overrides[target.key];
    if (override) {
      const sector = sectors.find((s) => s.id === override.sectorId);
      const company = [...companiesInIndustry, ...globalCompanies].find((c) => c.id === override.companyId);
      return {
        ...target,
        status: 'matched',
        companyId: override.companyId,
        sectorId: override.sectorId,
        resolvedIndustryName: industryName,
        resolvedSectorName: sector?.name || target.sectorName,
        message: company
          ? `Привязано: ${company.name} → ${sector?.name || target.sectorName}`
          : 'Компания добавлена и привязана',
      };
    }

    if (!target.sectorName) {
      return {
        ...target,
        status: 'sector_missing',
        message: 'В файле не указан сектор (столбец G)',
      };
    }

    if (!target.companyName) {
      return {
        ...target,
        status: 'company_not_found',
        message: 'В файле не указано название компании (столбец A)',
      };
    }

    const sector = findSectorByName(sectors, target.sectorName);
    if (!sector) {
      return {
        ...target,
        status: 'sector_not_found',
        message: `Сектор «${target.sectorName}» не найден в индустрии «${industryName}»`,
      };
    }

    const inIndustry = exactCompanyRows(companiesInIndustry, target.companyName);
    const exactInTargetSector = inIndustry.find((c) => c.sector_id === sector.id);

    if (exactInTargetSector) {
      return {
        ...target,
        status: 'matched',
        companyId: exactInTargetSector.id,
        sectorId: sector.id,
        resolvedIndustryName: industryName,
        resolvedSectorName: sector.name,
        message: `Найдена в БД: ${exactInTargetSector.name} (${industryName} → ${sector.name})`,
      };
    }

    const globalExact = exactCompanyRows(globalCompanies, target.companyName);

    if (inIndustry.length > 0) {
      const places = Array.from(new Set(inIndustry.map((c) => c.sector_name))).join(', ');
      return {
        ...target,
        status: 'wrong_sector',
        message: `Компания «${target.companyName}» есть в индустрии «${industryName}», но в другом секторе: ${places}. Ожидается сектор «${sector.name}».`,
        companyId: inIndustry[0].id,
        sectorId: sector.id,
      };
    }

    if (globalExact.length > 0) {
      const places = Array.from(new Set(globalExact.map((c) => `${c.industry_name} → ${c.sector_name}`))).join('; ');
      return {
        ...target,
        status: 'wrong_industry',
        message: `Компания «${target.companyName}» уже есть в БД, но в другой иерархии: ${places}. Ожидается: ${industryName} → ${sector.name}.`,
        companyId: globalExact[0].id,
        sectorId: sector.id,
      };
    }

    return {
      ...target,
      status: 'company_not_found',
      companyId: undefined,
      sectorId: sector.id,
      resolvedIndustryName: industryName,
      resolvedSectorName: sector.name,
      message: `Компания «${target.companyName}» не найдена в ${industryName} → ${sector.name}`,
    };
  });
}

export function isCompanyLinkError(status: CompanyLinkStatus): boolean {
  return status !== 'matched';
}

export function canAddCompanyFromExcel(status: CompanyLinkStatus): boolean {
  return status === 'company_not_found'
    || status === 'wrong_industry'
    || status === 'wrong_sector';
}

export function companyLinkForRow(
  resolutions: CompanyLinkResolution[],
  companyName: string | null,
  sectorName: string | null,
): CompanyLinkResolution | undefined {
  return resolutions.find((r) => r.key === buildCompanyLinkKey(companyName || '', sectorName || ''));
}
